import { z } from 'zod';
import type { Locale } from './enums';
import { localize } from './locale';

/**
 * Where the player lives. The game clock follows their real local time (a day starts at 5am) and
 * the weather is their city's real weather. Hong Kong, Taiwan and Australia for launch.
 */
export const CITIES = [
  { id: 'hong_kong', region: 'hk', zh: '香港', en: 'Hong Kong', tz: 'Asia/Hong_Kong', lat: 22.3193, lon: 114.1694 },
  { id: 'taipei', region: 'tw', zh: '台北', en: 'Taipei', tz: 'Asia/Taipei', lat: 25.033, lon: 121.5654 },
  { id: 'taichung', region: 'tw', zh: '台中', en: 'Taichung', tz: 'Asia/Taipei', lat: 24.1477, lon: 120.6736 },
  { id: 'tainan', region: 'tw', zh: '台南', en: 'Tainan', tz: 'Asia/Taipei', lat: 22.9999, lon: 120.227 },
  { id: 'kaohsiung', region: 'tw', zh: '高雄', en: 'Kaohsiung', tz: 'Asia/Taipei', lat: 22.6273, lon: 120.3014 },
  { id: 'sydney', region: 'au', zh: '悉尼', en: 'Sydney', tz: 'Australia/Sydney', lat: -33.8688, lon: 151.2093 },
  { id: 'melbourne', region: 'au', zh: '墨爾本', en: 'Melbourne', tz: 'Australia/Melbourne', lat: -37.8136, lon: 144.9631 },
  { id: 'brisbane', region: 'au', zh: '布里斯本', en: 'Brisbane', tz: 'Australia/Brisbane', lat: -27.4698, lon: 153.0251 },
  { id: 'perth', region: 'au', zh: '珀斯', en: 'Perth', tz: 'Australia/Perth', lat: -31.9505, lon: 115.8605 },
  { id: 'adelaide', region: 'au', zh: '阿德萊德', en: 'Adelaide', tz: 'Australia/Adelaide', lat: -34.9285, lon: 138.6007 },
] as const;

export type CityDef = (typeof CITIES)[number];
export const CityId = z.enum(CITIES.map((c) => c.id) as [CityDef['id'], ...CityDef['id'][]]);
export type CityId = z.infer<typeof CityId>;
export const DEFAULT_CITY: CityId = 'hong_kong';
export const CITY_REGIONS = ['hk', 'tw', 'au'] as const;
export type CityRegion = (typeof CITY_REGIONS)[number];

export const cityById = (id: string | null | undefined): CityDef => CITIES.find((c) => c.id === id) ?? CITIES[0];

/** Best guess from the device's time zone (first city in that zone), else Hong Kong. */
export const cityForTimeZone = (tz: string | null | undefined): CityId => CITIES.find((c) => c.tz === tz)?.id ?? DEFAULT_CITY;

export const cityName = (id: string, locale: Locale): string => {
  const c = cityById(id);
  return localize(locale, c.zh, c.en);
};
